import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  ArrowLeft, 
  Mail, 
  Phone,
  Briefcase,
  MapPin,
  Shield,
  Waves,
  Heart,
  Sailboat,
  Calendar,
  ExternalLink,
  Check,
  X,
  Edit2,
  Save,
  Upload,
  FileText,
  Trash2,
  Download,
  Syringe,
  HeartPulse,
  LifeBuoy,
  GripVertical,
  BookOpen,
  Utensils
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";

const certificationTypes = [
  { id: "cpr_aed", name: "CPR/AED", category: "general", icon: Shield },
  { id: "ceu", name: "CEU/CPR", category: "general", icon: BookOpen },
  { id: "cpr_aed_rte_combo", name: "AED/CPR/RTE Combo", category: "general", icon: Shield },
  { id: "cpr_aed_instructor", name: "CPR/AED Instructor", category: "general", icon: Shield },
  { id: "lifeguard", name: "Lifeguard Certification", category: "pool", icon: Waves },
  { id: "wsi", name: "Water Safety Instructor (WSI)", category: "wsi", icon: Waves },
  { id: "health_nursing", name: "Health/Nursing Certification", category: "health", icon: Heart },
  { id: "boating", name: "Boating Certification", category: "boating", icon: Sailboat },
  { id: "epipen", name: "Epi Pen/Anaphylaxis", category: "health", icon: Syringe },
  { id: "bls", name: "Basic Life Support (BLS)", category: "general", icon: HeartPulse },
  { id: "pal", name: "Pool Activity Leader (PAL)", category: "pool", icon: LifeBuoy },
  { id: "dining", name: "Dining Certification", category: "dining", icon: Utensils },
  { id: "bus_driver_dot", name: "Bus Driver DOT", category: "other", icon: Shield },
  { id: "working_papers", name: "Working Papers", category: "minor", icon: BookOpen },
];

function getCertStatus(cert, certTypeId, allCerts, campStart, campEnd) {
  // Helper: classify a date against camp season
  const classifyDate = (dateStr) => {
    if (!dateStr) return "covered"; // has cert but no exp date — treat as covered
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    if (d < today) return "expired";
    if (campEnd) {
      const ce = new Date(campEnd); ce.setHours(0, 0, 0, 0);
      // Expires on or BEFORE camp ends → needs renewal (covers "before camp" and "during camp")
      if (d <= ce) return "expiring_during_camp";
    }
    return "covered";
  };

  if (!cert?.has_cert) return "none";
  // Working Papers don't expire — green/covered as soon as uploaded
  if (certTypeId === "working_papers") return "covered";
  // WYSIWYG: status is driven by the cert's OWN displayed expiration date.
  // CEU-extending-CPR is handled at write time (backfill / upload), not at display.
  return classifyDate(cert.expiration_date);
}

function StatusIndicator({ status }) {
  const config = {
    none: { bg: "bg-slate-100", text: "text-slate-500", label: "No Certificate" },
    covered: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Covered Through Camp" },
    expiring_during_camp: { bg: "bg-amber-100", text: "text-amber-800", label: "Expires During Camp" },
    expired: { bg: "bg-red-100", text: "text-red-800", label: "Expired" },
  };

  const { bg, text, label } = config[status] || config.none;

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${bg} ${text}`} data-testid={`status-${status}`}>
      {label}
    </span>
  );
}

function FileUploadSection({ staffId, certTypeId, token, files, onUploadComplete }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const certFiles = files.filter(f => f.cert_type_id === certTypeId);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large. Max 25MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(
        `${API}/staff/${staffId}/certifications/${certTypeId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      if (res.data?.duplicate) {
        toast.info("File already uploaded — skipped duplicate");
      } else {
        toast.success("File uploaded successfully");
      }
      onUploadComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleView = async (fileRecord) => {
    try {
      const response = await axios.get(`${API}/files/${fileRecord.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 60000
      });
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Failed to open file");
    }
  };

  const handleDownload = async (fileRecord) => {
    try {
      const response = await axios.get(`${API}/files/${fileRecord.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 60000
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileRecord.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error("Download failed — try again");
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("File removed");
      onUploadComplete();
    } catch {
      toast.error("Failed to remove file");
    }
  };

  const onDragStart = (e, fileRecord) => {
    e.dataTransfer.setData("application/json", JSON.stringify({
      fileId: fileRecord.id,
      fileName: fileRecord.original_filename,
      fromCertType: certTypeId
    }));
    e.dataTransfer.effectAllowed = "move";
  };

  return (
    <div className="mt-3 space-y-2">
      {certFiles.length > 0 && (
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
          Uploaded Files ({certFiles.length})
        </p>
      )}
      {certFiles.map((f) => (
        <div
          key={f.id}
          draggable
          onDragStart={(e) => onDragStart(e, f)}
          className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-md text-sm border border-slate-200 cursor-grab active:cursor-grabbing hover:border-brand-navy/30 transition-colors"
          data-testid={`file-item-${f.id}`}
        >
          <GripVertical className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
          <FileText className="h-4 w-4 text-brand-navy flex-shrink-0" />
          <span className="truncate flex-1 text-slate-700 font-medium">{f.original_filename}</span>
          <button
            onClick={() => handleView(f)}
            className="text-brand-navy hover:text-brand-navy/70 p-1 flex items-center gap-1 text-xs font-medium rounded hover:bg-slate-100"
            title="View file"
            data-testid={`view-file-${f.id}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">View</span>
          </button>
          <button
            onClick={() => handleDownload(f)}
            className="text-emerald-600 hover:text-emerald-800 p-1 flex items-center gap-1 text-xs font-medium rounded hover:bg-emerald-50"
            title="Download file"
            data-testid={`download-file-${f.id}`}
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Download</span>
          </button>
          <button
            onClick={() => handleDelete(f.id)}
            className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
            title="Remove file"
            data-testid={`delete-file-${f.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleUpload}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt,.csv,.xls,.xlsx"
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full text-xs"
        data-testid={`upload-cert-file-${certTypeId}`}
      >
        <Upload className="h-3.5 w-3.5 mr-1.5" />
        {uploading ? "Uploading..." : "Upload File"}
      </Button>
    </div>
  );
}

export default function StaffDetailPage() {
  const { staffId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [staffFiles, setStaffFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCertDialog, setEditCertDialog] = useState(null);
  const [campStart, setCampStart] = useState("2026-06-29");
  const [campEnd, setCampEnd] = useState("2026-08-20");
  const [certForm, setCertForm] = useState({
    has_cert: false,
    expiration_date: null,
    date_completed: null,
    signed_up_for_renewal: false,
    google_drive_file_url: ""
  });

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data);
    } catch (error) {
      toast.error("Failed to load staff details");
      navigate("/staff");
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/staff/${staffId}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffFiles(response.data);
    } catch {
      // Silent fail - files are supplementary
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchFiles();
    axios.get(`${API}/settings/camp-dates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setCampStart(res.data.camp_start);
        setCampEnd(res.data.camp_end);
      })
      .catch(() => { /* keep defaults */ });
  }, [staffId, token]);

  const openCertEditor = (certType) => {
    const currentCert = staff?.certifications?.[certType.id] || {};
    setCertForm({
      has_cert: currentCert.has_cert || false,
      expiration_date: currentCert.expiration_date ? new Date(currentCert.expiration_date) : null,
      date_completed: currentCert.date_completed ? new Date(currentCert.date_completed) : null,
      signed_up_for_renewal: currentCert.signed_up_for_renewal || false,
      google_drive_file_url: currentCert.google_drive_file_url || ""
    });
    setEditCertDialog(certType);
  };

  const saveCertification = async () => {
    try {
      // Bus Driver DOT auto-sets has_cert=true if an expiration date is provided
      const isBusDot = editCertDialog.id === "bus_driver_dot";
      const hasCert = isBusDot ? !!certForm.expiration_date : certForm.has_cert;
      await axios.post(
        `${API}/staff/${staffId}/certifications`,
        {
          cert_type_id: editCertDialog.id,
          has_cert: hasCert,
          expiration_date: certForm.expiration_date
            ? certForm.expiration_date.toISOString()
            : null,
          date_completed: certForm.date_completed
            ? certForm.date_completed.toISOString()
            : null,
          signed_up_for_renewal: certForm.signed_up_for_renewal,
          google_drive_file_url: certForm.google_drive_file_url
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Certification updated");
      setEditCertDialog(null);
      fetchStaff();
    } catch (error) {
      toast.error("Failed to update certification");
    }
  };

  const updateCprClassSignup = async (signedUp) => {
    try {
      await axios.put(
        `${API}/staff/${staffId}/cpr-class-signup?signed_up=${signedUp}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("CPR class signup status updated");
      fetchStaff();
    } catch (error) {
      toast.error("Failed to update CPR class signup status");
    }
  };

  const handleFilesChanged = () => {
    fetchFiles();
    fetchStaff();
  };

  const toggleWorkingPapers = async (checked) => {
    try {
      await axios.post(
        `${API}/staff/${staffId}/certifications`,
        {
          cert_type_id: "working_papers",
          has_cert: !!checked,
          expiration_date: null,
          date_completed: null,
          signed_up_for_renewal: false,
          google_drive_file_url: ""
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(checked ? "Working Papers marked received" : "Working Papers unmarked");
      fetchStaff();
    } catch (error) {
      toast.error("Failed to update Working Papers");
    }
  };

  const handleReassignFile = async (fileId, fromCertType, toCertType) => {
    if (fromCertType === toCertType) return;
    try {
      await axios.put(
        `${API}/files/${fileId}/reassign`,
        { new_cert_type: toCertType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fromName = certificationTypes.find(c => c.id === fromCertType)?.name || fromCertType;
      const toName = certificationTypes.find(c => c.id === toCertType)?.name || toCertType;
      toast.success(`Moved file from ${fromName} to ${toName}`);
      fetchStaff();
      fetchFiles();
    } catch {
      toast.error("Failed to move file");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy"></div>
      </div>
    );
  }

  if (!staff) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/staff")}
        className="text-slate-600 hover:text-slate-900 -ml-2"
        data-testid="back-to-staff-list"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Staff List
      </Button>

      {/* Staff Header Card */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="staff-detail-card">
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          {/* Avatar */}
          <div className="h-20 w-20 rounded-full bg-brand-navy/10 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl font-heading font-bold text-brand-navy">
              {staff.name?.split(" ").map(n => n[0]).join("").toUpperCase()}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
              {staff.last_name && staff.first_name
                ? `${staff.last_name}, ${staff.first_name}`
                : staff.name}
            </h1>
            
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
              {staff.position && (
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-slate-400" />
                  {staff.position}
                </div>
              )}
              {staff.division && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  {staff.division}
                </div>
              )}
              {staff.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {staff.email}
                </div>
              )}
              {staff.phone && (
                <div className="flex items-center gap-2" data-testid="staff-phone">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <a href={`tel:${staff.phone.replace(/\s/g, '')}`} className="hover:text-blue-600 transition-colors">
                    {staff.phone}
                  </a>
                </div>
              )}
            </div>

            {/* CPR Class Info from CampMinder - Prominent display */}
            {(staff.cpr_first_aid_class_date || staff.cpr_only_date) && (
              <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="cpr-class-info-box">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  CPR Class Schedule
                </p>
                {staff.cpr_first_aid_class_date && (
                  <div className="flex items-center gap-2 bg-white/80 rounded-md px-3 py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white">
                      CPR & First Aid
                    </span>
                    <span className="text-sm font-semibold text-blue-900">{staff.cpr_first_aid_class_date}</span>
                  </div>
                )}
                {staff.cpr_only_date && (
                  <div className="flex items-center gap-2 bg-white/80 rounded-md px-3 py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-600 text-white">
                      CPR Only
                    </span>
                    <span className="text-sm font-semibold text-indigo-900">{staff.cpr_only_date}</span>
                  </div>
                )}
              </div>
            )}

            {/* CPR Class Signup */}
            <div className="mt-4 flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="cpr-signup"
                checked={staff.signed_up_for_cpr_class || false}
                onCheckedChange={updateCprClassSignup}
                data-testid="cpr-class-signup-checkbox"
              />
              <Label htmlFor="cpr-signup" className="text-sm font-medium text-slate-700 cursor-pointer">
                Signed Up for CPR Renewal Class
              </Label>
            </div>
          </div>
        </div>
      </div>

      {/* Certifications Grid */}
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
          Certifications
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certificationTypes.filter((ct) => {
            // Dining cert only shows for staff in the Dining division
            if (ct.id === "dining") {
              const div = (staff.division || "").toLowerCase();
              return div.includes("dining");
            }
            // Working Papers only shows for staff aged 15/16/17 at camp start
            if (ct.id === "working_papers") {
              if (!staff.date_of_birth) return false;
              const ref = campStart ? new Date(campStart) : new Date("2026-06-29");
              const dob = new Date(staff.date_of_birth);
              if (isNaN(dob.getTime())) return false;
              let age = ref.getFullYear() - dob.getFullYear();
              const m = ref.getMonth() - dob.getMonth();
              if (m < 0 || (m === 0 && ref.getDate() < dob.getDate())) age--;
              return age === 15 || age === 16 || age === 17;
            }
            return true;
          }).map((certType) => {
            const cert = staff.certifications?.[certType.id] || {};
            const status = getCertStatus(cert, certType.id, staff.certifications, campStart, campEnd);
            const Icon = certType.icon;
            
            const onDragOver = (e) => {
              e.preventDefault();
              e.currentTarget.classList.add("ring-2", "ring-brand-navy", "ring-offset-2", "bg-blue-50/50");
            };
            const onDragLeave = (e) => {
              e.currentTarget.classList.remove("ring-2", "ring-brand-navy", "ring-offset-2", "bg-blue-50/50");
            };
            const onDrop = (e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("ring-2", "ring-brand-navy", "ring-offset-2", "bg-blue-50/50");
              try {
                const data = JSON.parse(e.dataTransfer.getData("application/json"));
                if (data.fileId && data.fromCertType !== certType.id) {
                  handleReassignFile(data.fileId, data.fromCertType, certType.id);
                }
              } catch {}
            };

            return (
              <div
                key={certType.id}
                className="bg-white border border-slate-200 rounded-lg p-5 transition-all duration-150"
                data-testid={`cert-card-${certType.id}`}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-brand-navy/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-brand-navy" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{certType.name}</h3>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">
                        {certType.category}
                      </p>
                    </div>
                  </div>
                  {certType.id !== "working_papers" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openCertEditor(certType)}
                      data-testid={`edit-cert-${certType.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Status</span>
                    <StatusIndicator status={status} />
                  </div>

                  {certType.id !== "bus_driver_dot" && certType.id !== "working_papers" && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Has Certificate</span>
                      {cert.has_cert ? (
                        <Check className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <X className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  )}

                  {certType.id === "working_papers" && (
                    <div className="space-y-2">
                      <span className="text-sm text-slate-500">Working Papers Received?</span>
                      <div className="flex items-center gap-6">
                        <label
                          className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border-2 transition-colors flex-1 justify-center ${
                            cert.has_cert
                              ? "bg-emerald-50 border-emerald-400 text-emerald-800"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                          data-testid="working-papers-yes"
                        >
                          <Checkbox
                            checked={!!cert.has_cert}
                            onCheckedChange={(v) => v && toggleWorkingPapers(true)}
                            className="h-5 w-5"
                          />
                          <span className="font-semibold">Yes</span>
                        </label>
                        <label
                          className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border-2 transition-colors flex-1 justify-center ${
                            !cert.has_cert
                              ? "bg-red-50 border-red-400 text-red-800"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                          data-testid="working-papers-no"
                        >
                          <Checkbox
                            checked={!cert.has_cert}
                            onCheckedChange={(v) => v && toggleWorkingPapers(false)}
                            className="h-5 w-5"
                          />
                          <span className="font-semibold">No</span>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {cert.date_completed && certType.id !== "bus_driver_dot" && certType.id !== "working_papers" && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Date Completed</span>
                      <span className="text-sm font-medium text-slate-900">
                        {format(new Date(cert.date_completed), "MMM d, yyyy")}
                      </span>
                    </div>
                  )}
                  
                  {cert.expiration_date && certType.id !== "working_papers" && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Expiration</span>
                      <span className="text-sm font-medium text-slate-900">
                        {format(new Date(cert.expiration_date), "MMM d, yyyy")}
                      </span>
                    </div>
                  )}

                  {cert.has_cert && !cert.expiration_date && certType.id !== "working_papers" && (
                    <div
                      className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between gap-3"
                      data-testid={`missing-date-warning-${certType.id}`}
                    >
                      <div className="flex items-center gap-2 text-amber-800 text-sm">
                        <Calendar className="h-4 w-4" />
                        <span className="font-medium">Expiration Date Missing</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-800 hover:bg-amber-100"
                        onClick={() => openCertEditor(certType)}
                        data-testid={`add-expiration-${certType.id}`}
                      >
                        Add Date
                      </Button>
                    </div>
                  )}
                  
                  {certType.id.includes("cpr") && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Renewal Signup</span>
                      {cert.signed_up_for_renewal ? (
                        <Check className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <X className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  )}
                  
                  {cert.google_drive_file_url && certType.id !== "bus_driver_dot" && certType.id !== "working_papers" && (
                    <a
                      href={cert.google_drive_file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-brand-navy hover:underline mt-2"
                      data-testid={`cert-file-link-${certType.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Certificate File
                    </a>
                  )}

                  {/* File Upload Section — hidden for Bus Driver DOT (date-only) and Working Papers (checkbox-only) */}
                  {certType.id !== "bus_driver_dot" && certType.id !== "working_papers" && (
                    <FileUploadSection
                      staffId={staffId}
                      certTypeId={certType.id}
                      token={token}
                      files={staffFiles}
                      onUploadComplete={handleFilesChanged}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Certification Dialog */}
      <Dialog open={!!editCertDialog} onOpenChange={(open) => !open && setEditCertDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Edit {editCertDialog?.name}
            </DialogTitle>
            <DialogDescription>
              Update certification details for {staff.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editCertDialog?.id !== "bus_driver_dot" && (
              <div className="flex items-center gap-3">
                <Checkbox
                  id="has_cert"
                  checked={certForm.has_cert}
                  onCheckedChange={(checked) => setCertForm({ ...certForm, has_cert: checked })}
                  data-testid="edit-cert-has-cert"
                />
                <Label htmlFor="has_cert">Has this certification</Label>
              </div>
            )}

            {(certForm.has_cert || editCertDialog?.id === "bus_driver_dot") && (
              <>
                {editCertDialog?.id !== "bus_driver_dot" && editCertDialog?.id !== "working_papers" && (
                  <div className="space-y-2">
                    <Label htmlFor="date_completed_input">Date Completed</Label>
                    <Input
                      id="date_completed_input"
                      type="date"
                      value={certForm.date_completed ? format(certForm.date_completed, "yyyy-MM-dd") : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCertForm({ ...certForm, date_completed: v ? new Date(v + "T00:00:00") : null });
                      }}
                      className="w-full text-base"
                      data-testid="edit-cert-date-completed"
                    />
                  </div>
                )}

                {editCertDialog?.id !== "working_papers" && (
                <div className="space-y-2">
                  <Label htmlFor="expiration_date_input">Expiration Date</Label>
                  <Input
                    id="expiration_date_input"
                    type="date"
                    value={certForm.expiration_date ? format(certForm.expiration_date, "yyyy-MM-dd") : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCertForm({ ...certForm, expiration_date: v ? new Date(v + "T00:00:00") : null });
                    }}
                    className="w-full text-base"
                    data-testid="edit-cert-expiration-date"
                  />
                </div>
                )}

                {editCertDialog?.id.includes("cpr") && (
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="renewal_signup"
                      checked={certForm.signed_up_for_renewal}
                      onCheckedChange={(checked) => 
                        setCertForm({ ...certForm, signed_up_for_renewal: checked })
                      }
                      data-testid="edit-cert-renewal-signup"
                    />
                    <Label htmlFor="renewal_signup">Signed up for renewal class</Label>
                  </div>
                )}

                {editCertDialog?.id !== "bus_driver_dot" && editCertDialog?.id !== "working_papers" && (
                  <div className="space-y-2">
                    <Label htmlFor="drive_url">Google Drive File URL</Label>
                    <Input
                      id="drive_url"
                      placeholder="https://drive.google.com/..."
                      value={certForm.google_drive_file_url}
                      onChange={(e) =>
                        setCertForm({ ...certForm, google_drive_file_url: e.target.value })
                      }
                      data-testid="edit-cert-drive-url"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCertDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={saveCertification}
              className="bg-brand-navy hover:bg-brand-navy/90"
              data-testid="save-cert-button"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
